<template>
  <RfBaseCard class="justify-start gap-3! self-stretch">
    <span class="flex grow-0 items-center justify-between">
      <span
        v-if="Array.isArray(props.title)"
        ref="buttonContainer"
        role="tablist"
        class="relative inline-flex items-center"
        @keydown="onTabKeydown"
      >
        <button
          v-for="(title, index) in props.title"
          :id="`rf-dashboard-tab-${uid}-${title.key}`"
          :key="title.key"
          ref="buttons"
          role="tab"
          :aria-selected="selectedTab.key === title.key"
          :aria-controls="`rf-dashboard-panel-${uid}-${title.key}`"
          :tabindex="selectedTab.key === title.key ? 0 : -1"
          class="text-action-buttons text-beige-50 px-4 pt-2 pb-3"
          :class="{ 'text-beige-90 font-semibold!': selectedTab.key === title.key }"
          @click="selectTab(index)"
        >
          {{ title.value }}
        </button>
        <span
          ref="slider"
          aria-hidden="true"
          class="bg-yellow absolute top-auto bottom-0 h-0.5 w-10 transition-all duration-300"
        ></span>
      </span>
      <h2 v-else class="text-heading-1 text-beige-90">{{ props.title }}</h2>
      <slot name="more"></slot>
    </span>
    <div v-if="$slots.body" class="h-full">
      <slot name="body"></slot>
    </div>
    <template v-if="Array.isArray(props.title)">
      <Transition
        v-for="slot in props.title"
        :key="slot.key"
        name="fade"
        tag="div"
        class="h-full w-full"
      >
        <div
          v-show="selectedTab.key === slot.key"
          :id="`rf-dashboard-panel-${uid}-${slot.key}`"
          :key="slot.key"
          role="tabpanel"
          :aria-labelledby="`rf-dashboard-tab-${uid}-${slot.key}`"
          class="h-full min-h-px w-full"
        >
          <slot :name="slot.key"></slot>
        </div>
      </Transition>
    </template>
  </RfBaseCard>
</template>

<script setup>
import { getCurrentInstance, onMounted, ref, watchPostEffect } from "vue";
import RfBaseCard from "@/components/cards/RfBaseCard.vue";

const { uid } = getCurrentInstance();
const emit = defineEmits(["change"]);

const buttons = ref();
const slider = ref();
const buttonContainer = ref();
const selectedTab = ref({ key: "", index: 0 });
const props = defineProps({ title: { type: [String, Array], default: "Title" } });

Array.isArray(props.title) &&
  onMounted(() => (selectedTab.value = { key: props.title[0].key, index: 0 }));

const selectTab = index => {
  selectedTab.value = { key: props.title[index].key, index };
  emit("change", selectedTab.value);
};

const onTabKeydown = e => {
  const count = props.title.length;
  const nextIndex = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
  const targetIndex = { Home: 0, End: count - 1 }[e.key];
  const index = targetIndex ?? (nextIndex && (selectedTab.value.index + nextIndex + count) % count);

  if (index === undefined || index === null) return;
  e.preventDefault();
  selectTab(index);
  buttons.value?.[index]?.focus();
};

watchPostEffect(() => {
  const button = buttons.value?.[selectedTab.value.index];
  if (!button || !buttonContainer.value || !slider.value) return;
  const { left: childLeft, width } = button.getBoundingClientRect();
  const { left: parentLeft } = buttonContainer.value.getBoundingClientRect();
  slider.value.style.width = `${width}px`;
  slider.value.style.transform = `translateX(${childLeft - parentLeft}px)`;
});

defineExpose({ selectedTab });
</script>
